import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteForbidden,
    getRouteReportsSpendSummary,
    getRouteReportsSpendSummaryCategory,
    getRouteReportsSpendSummaryProduct,
    getRouteReportsSpendSummaryProperty,
    getRouteReportsSpendSummarySupplier,
} from 'shared/const/router';
import { clampToZero } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { getLastDrillCrumbLabel, withDrillTrail } from 'shared/lib/reports';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import {
    ReportDrillInMetrics,
    ReportDrillInSections,
} from 'shared/ui/ReportDrillInLayout';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { Page } from 'widgets/Page';
import { ProductDetailsDrawer } from 'widgets/ProductDetailsDrawer';
import { navigateSpendDrillBack } from 'widgets/SpendSummaryReport/model/lib/spendDrillInNavigation';
import {
    getSpendCategoryTitle,
    mergeSpendDrillInScope,
} from 'widgets/SpendSummaryReport/model/types/spendDrillIn';
import { SpendDrillInBackNav } from 'widgets/SpendSummaryReport/ui/SpendDrillInBackNav';
import { SpendSummaryCategoriesSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryCategoriesSection';
import { SpendSummaryProductsSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryProductsSection';
import { SpendSummarySuppliersSection } from 'widgets/SpendSummaryReport/ui/SpendSummarySuppliersSection';
import { SpendSummaryTotalSpendMetric } from 'widgets/SpendSummaryReport/ui/SpendSummaryTotalSpendMetric';

import { useReportsPageSpendSummaryProperty } from '../model/hooks/useReportsPageSpendSummaryProperty';

import type { SpendProductRowDto } from 'entities/ReportSpendSummary';
import type { ReportWidgetInteractionPayload } from 'widgets/ComposableReportWidget';
const ReportsPageSpendSummaryProperty = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const {
        isSpendSummaryVisible,
        propertyId,
        propertyName,
        loading,
        summary,
        byCategory,
        bySupplier,
        products,
        totalSpend,
        spendPerUnit,
        spendPerUnitPerMonth,
        orderCount,
        spendPerOrder,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
        locationState,
        showCategorySection,
        showSupplierSection,
    } = useReportsPageSpendSummaryProperty();

    const childScope = useMemo(
        () =>
            mergeSpendDrillInScope(locationState.scope, {
                propertyIds: propertyId ? [propertyId] : undefined,
            }),
        [locationState.scope, propertyId],
    );

    const drillParent = useMemo(
        () => ({
            path: getRouteReportsSpendSummaryProperty(propertyId ?? ''),
            label: propertyName,
        }),
        [propertyId, propertyName],
    );

    const handleBack = useCallback(() => {
        navigateSpendDrillBack(navigate, locationState.trail);
    }, [locationState.trail, navigate]);

    const handleCategorySelect = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const categoryId = payload.item.meta?.categoryId;
            if (typeof categoryId !== 'string') {
                return;
            }

            navigate(getRouteReportsSpendSummaryCategory(categoryId), {
                state: withDrillTrail(locationState, drillParent, {
                    scope: childScope,
                    contextTotalSpend: summary?.totalSpend,
                    categoryTitle: getSpendCategoryTitle(
                        payload.item.meta?.categoryTitle,
                        payload.item.label,
                    ),
                }),
            });
        },
        [childScope, drillParent, locationState, navigate, summary?.totalSpend],
    );

    const handleSupplierSelect = useCallback(
        ({
            supplierId,
            supplierName,
        }: {
            supplierId: string;
            supplierName?: string;
        }) => {
            navigate(getRouteReportsSpendSummarySupplier(supplierId), {
                state: withDrillTrail(locationState, drillParent, {
                    scope: childScope,
                    contextTotalSpend: summary?.totalSpend,
                    supplierName,
                }),
            });
        },
        [childScope, drillParent, locationState, navigate, summary?.totalSpend],
    );

    const handleProductRowClick = useCallback(
        (sku: string, product: SpendProductRowDto) => {
            navigate(getRouteReportsSpendSummaryProduct(sku), {
                state: withDrillTrail(locationState, drillParent, {
                    scope: childScope,
                    contextTotalSpend: summary?.totalSpend,
                    product,
                }),
            });
        },
        [childScope, drillParent, locationState, navigate, summary?.totalSpend],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSpendSummaryVisible === undefined) {
        return null;
    }

    if (!isSpendSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!propertyId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSpendSummary()} replace />;
    }

    const clampedSpendPerUnit =
        spendPerUnit == null ? null : clampToZero(spendPerUnit);
    const clampedSpendPerUnitPerMonth =
        spendPerUnitPerMonth == null ? null : clampToZero(spendPerUnitPerMonth);

    return (
        <Page
            dataTestId="reports-page-spend-summary-property"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={propertyName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={getLastDrillCrumbLabel(
                        locationState.trail,
                        t('spendSummary.reportTitle'),
                    )}
                    backButtonId="back-to-spend-summary-button"
                    backSlot={
                        <SpendDrillInBackNav
                            trail={locationState.trail}
                            onBack={handleBack}
                            id="back-to-spend-summary-button"
                        />
                    }
                />

                <ReportDrillInMetrics>
                    <SpendSummaryTotalSpendMetric
                        summary={summary}
                        primaryAmount={totalSpend}
                        loading={loading}
                        showSavingsDetail={false}
                    />
                    <ReportMetric
                        title={t('spendSummary.spendPerUnit')}
                        primary={
                            clampedSpendPerUnit == null
                                ? '-'
                                : formatReportCurrency(clampedSpendPerUnit)
                        }
                        secondary={
                            clampedSpendPerUnitPerMonth === null
                                ? '-'
                                : `${formatReportCurrency(
                                      clampedSpendPerUnitPerMonth,
                                  )} ${t('spendSummary.perUnitPerMonthSuffix')}`
                        }
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('spendSummary.spendPerOrder')}
                        primary={
                            spendPerOrder == null
                                ? '-'
                                : formatReportCurrency(spendPerOrder)
                        }
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('Orders')}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                </ReportDrillInMetrics>

                <ReportDrillInSections>
                    {showCategorySection || showSupplierSection ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '24px',
                            }}
                        >
                            {showCategorySection ? (
                                <Box sx={{ flex: 1, minWidth: '360px' }}>
                                    <SpendSummaryCategoriesSection
                                        byCategory={byCategory}
                                        loading={loading}
                                        onCategorySelect={handleCategorySelect}
                                        tableOnly
                                        tableColumnIds={[
                                            'category',
                                            'glCode',
                                            'spend',
                                            'percentSpend',
                                        ]}
                                        showBottomBorder={false}
                                    />
                                </Box>
                            ) : null}
                            {showSupplierSection ? (
                                <Box sx={{ flex: 1, minWidth: '360px' }}>
                                    <SpendSummarySuppliersSection
                                        bySupplier={bySupplier}
                                        totalSpend={summary?.totalSpend}
                                        loading={loading}
                                        onSupplierSelect={handleSupplierSelect}
                                        tableOnly
                                        tableColumnIds={[
                                            'supplier',
                                            'spend',
                                            'percentSpend',
                                        ]}
                                        showBottomBorder={false}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}
                    <SpendSummaryProductsSection
                        products={products}
                        totalSpend={summary?.totalSpend}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'quantity',
                            'totalSpend',
                            'percentSpend',
                        ]}
                        showBottomBorder={false}
                    />
                    <ProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </ReportDrillInSections>
            </Box>
        </Page>
    );
};

export default ReportsPageSpendSummaryProperty;
